import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true, // Siempre true, no se puede desactivar
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    } else {
      try {
        const savedSettings = JSON.parse(consent);
        setSettings(savedSettings);
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setSettings(allAccepted);
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(settings));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleDecline = () => {
    const minimalSettings = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(minimalSettings));
    setSettings(minimalSettings);
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end justify-center sm:items-center p-4 z-50">
      <div className="bg-dark-card rounded-lg shadow-xl max-w-2xl w-full">
        {showSettings ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <Settings className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold text-white">Configuración de Cookies</h2>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Cookies Necesarias</h3>
                  <p className="text-sm text-gray-400">Requeridas para el funcionamiento básico del sitio</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settings.necessary}
                    disabled
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Cookies Analíticas</h3>
                  <p className="text-sm text-gray-400">Nos ayudan a mejorar el sitio web</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settings.analytics}
                    onChange={(e) => setSettings({ ...settings, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary cursor-pointer"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Cookies de Marketing</h3>
                  <p className="text-sm text-gray-400">Utilizadas para publicidad personalizada</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settings.marketing}
                    onChange={(e) => setSettings({ ...settings, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary cursor-pointer"></div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={handleDecline}
                className="text-gray-400 hover:text-white px-4 py-2"
              >
                Rechazar Todo
              </button>
              <button
                onClick={handleSaveSettings}
                className="btn-primary"
              >
                Guardar Preferencias
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Cookie className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold text-white">Política de Cookies</h2>
            </div>
            <p className="text-gray-300 text-sm mb-6">
              Utilizamos cookies para mejorar tu experiencia en nuestra plataforma. Algunas son necesarias para el funcionamiento del sitio, mientras que otras nos ayudan a mejorarlo. Puedes aceptar todas las cookies o configurar tus preferencias. Para más información, consulta nuestra{' '}
              <a href="/privacy" className="text-primary hover:text-primary-hover">
                Política de Privacidad
              </a>.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-end space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleDecline}
                className="text-gray-400 hover:text-white"
              >
                Solo Necesarias
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="text-gray-400 hover:text-white"
              >
                Configurar
              </button>
              <button
                onClick={handleAcceptAll}
                className="btn-primary"
              >
                Aceptar Todas
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;